import { supabase } from "@/integrations/supabase/client";
import { Procurement } from "@/types/database.types";
import { getProcurementById } from "@/services/procurementService";
import { 
  sendTelegramNotification, 
  formatStatusChangeNotification 
} from "@/services/telegramService";
import { createProcurementAudit } from "@/services/procurementAuditService";
import { 
  checkAndCreateDiscrepancy, 
  sendDiscrepancyNotification 
} from "@/services/discrepancyService";
import { recordInboundWithBatchLayer } from '@/services/inventoryService';
import type { ItemWarehouseData } from '@/types/database/procurement-item-warehouse';

/**
 * Updates procurement status based on business rules for warehouse confirmation/rejection.
 * Rules:
 *   - TERM: pending_warehouse → confirm → pending_finance → confirm → completed
 *   - UPFRONT: pending_finance → confirm → pending_warehouse → confirm → completed
 */
export async function updateWarehouseStatus(options: {
  procurement: Procurement;
  inspectionData?: {
    receivedQuantityKg?: number;
    inspectionNotes?: string;
    rejectionReason?: string;
  };
  itemWarehouseData?: ItemWarehouseData[];
  userId: string;
  action: "confirm" | "reject";
}) {
  const { procurement, inspectionData = {}, itemWarehouseData, userId, action } = options;
  if (!procurement || !userId) throw new Error("Missing procurement or user");

  // Enhanced debugging - let's see what we actually have
  console.log(`[WAREHOUSE] Raw procurement data:`, {
    id: procurement.id,
    status: procurement.status,
    finance: procurement.finance,
    financePaymentTermType: procurement.finance?.payment_term_type
  });

  // Fetch fresh finance data to ensure we have the latest payment_term_type
  const { data: financeData, error: financeError } = await supabase
    .from("procurement_finance")
    .select("payment_term_type")
    .eq("procurement_id", procurement.id)
    .maybeSingle();

  if (financeError) {
    console.error("[WAREHOUSE] Error fetching finance data:", financeError);
  }

  // Data integrity warnings
  if (!financeData) {
    console.warn("[WAREHOUSE] WARNING: No procurement_finance record found for:", procurement.id);
  }

  if (financeData && !financeData.payment_term_type) {
    console.warn("[WAREHOUSE] WARNING: procurement_finance exists but payment_term_type is null:", procurement.id);
  }

  console.log(`[WAREHOUSE] Fresh finance data from DB:`, financeData);

  // Use fresh data if available, otherwise fall back to procurement.finance
  const actualPaymentTermType = financeData?.payment_term_type || procurement.finance?.payment_term_type;
  
  // Determine payment type with more explicit logic
  const paymentType = actualPaymentTermType?.toLowerCase() === "term" ? "term" : "upfront";
  
  console.log(`[WAREHOUSE] Payment type determination:`, {
    actualPaymentTermType,
    resolvedPaymentType: paymentType,
    procurementId: procurement.id
  });

  let newStatus: string = procurement.status;
  const oldStatus = procurement.status;

  // Confirmation flow
  if (action === "confirm") {
    if (procurement.status === "pending_warehouse") {
      // For term payments, verify quality inspection was completed before warehouse confirmation
      if (paymentType === "term") {
        const { data: qualityData, error: qualityCheckError } = await supabase
          .from("procurement_quality")
          .select("id, grading_type, compensation_kg, preliminary_compensation_kg")
          .eq("procurement_id", procurement.id)
          .maybeSingle();
        
        if (qualityCheckError) {
          console.error("[WAREHOUSE] Error checking quality data:", qualityCheckError);
        }
        
        if (!qualityData) {
          console.error("[WAREHOUSE] No quality inspection data found for term payment procurement:", procurement.id);
          throw new Error(
            "Cannot confirm warehouse: Quality inspection has not been completed. " +
            "Please ensure quality inspection is done before warehouse confirmation."
          );
        }
        
        console.log(`[WAREHOUSE] Quality data verified for term payment:`, qualityData);
        
        // TERM: warehouse confirms first, then goes to finance
        newStatus = "pending_finance";
        console.log(`[WAREHOUSE] TERM payment: ${procurement.id} going from pending_warehouse to pending_finance`);
      } else if (paymentType === "upfront") {
        // UPFRONT: Validate that finance has approved and paid before completing
        const { data: financeApprovalData } = await supabase
          .from("procurement_finance")
          .select("approved_by, payment_confirmed, expense_id, approved_net_payable_amount, has_advance_payment, advance_payment_confirmed")
          .eq("procurement_id", procurement.id)
          .maybeSingle();
        
        // For advance-payment procurements, only require advance_payment_confirmed (not full payment_confirmed)
        const isPaymentSatisfied = financeApprovalData?.has_advance_payment
          ? financeApprovalData?.advance_payment_confirmed
          : financeApprovalData?.payment_confirmed;
        
        if (!financeApprovalData?.approved_by || !isPaymentSatisfied) {
          console.error("[WAREHOUSE] Cannot complete upfront procurement - finance approval missing:", procurement.id);
          throw new Error(
            "Cannot complete procurement: Finance approval and payment confirmation is required. " +
            "Please ensure finance has approved and paid this procurement before warehouse confirmation."
          );
        }
        
        if (!financeApprovalData?.expense_id) {
          console.error("[WAREHOUSE] Cannot complete upfront procurement - expense not created:", procurement.id);
          throw new Error(
            "Cannot complete procurement: No expense record found. " +
            "Please ensure the finance team has processed the payment correctly."
          );
        }

        // Check for payment discrepancy after warehouse confirmation for upfront payments
        const originalPaidAmount = financeApprovalData?.approved_net_payable_amount || 0;
        if (originalPaidAmount > 0) {
          try {
            console.log('[WAREHOUSE] Checking for upfront payment discrepancy...');
            const discrepancyResult = await checkAndCreateDiscrepancy(
              procurement.id,
              originalPaidAmount
            );
            
            if (discrepancyResult.hasDiscrepancy && discrepancyResult.discrepancy) {
              console.log('[WAREHOUSE] Discrepancy detected:', discrepancyResult.discrepancy);
              // Send notification asynchronously
              sendDiscrepancyNotification(
                procurement.id,
                discrepancyResult.discrepancy.original_paid_amount,
                discrepancyResult.discrepancy.final_calculated_amount,
                discrepancyResult.discrepancy.discrepancy_amount
              ).catch(err => console.error('[WAREHOUSE] Failed to send discrepancy notification:', err));
            }
          } catch (discError) {
            // Don't block warehouse confirmation if discrepancy check fails
            console.error('[WAREHOUSE] Error checking discrepancy:', discError);
          }
        }
        
        // UPFRONT: warehouse confirms last, goes to completed
        newStatus = "completed";
        console.log(`[WAREHOUSE] UPFRONT payment: ${procurement.id} going from pending_warehouse to completed`);
      }
    } else if (procurement.status === "pending_finance") {
      // Warehouse cannot confirm when status is pending_finance
      throw new Error("Warehouse cannot confirm procurements in pending_finance status");
    } else if (procurement.status === "completed" || procurement.status === "rejected") {
      throw new Error("Cannot confirm a completed or rejected procurement");
    } else {
      throw new Error(`Cannot confirm procurement with status: ${procurement.status}`);
    }
  } else if (action === "reject") {
    // Any status can be rejected by warehouse
    newStatus = "rejected";
  }

  console.log(`[WAREHOUSE] Final status decision:`, {
    procurementId: procurement.id,
    currentStatus: procurement.status,
    paymentType,
    newStatus,
    action
  });

  // Start a transaction to ensure data consistency
  try {
    // Calculate net payable from received quantity and quality compensation
    let calculatedNetKgPayable = inspectionData.receivedQuantityKg ?? procurement.total_quantity ?? 0;
    
    // Fetch quality compensation to calculate net payable
    const { data: qualityData } = await supabase
      .from("procurement_quality")
      .select("compensation_kg, preliminary_compensation_kg")
      .eq("procurement_id", procurement.id)
      .maybeSingle();
    
    const compensationKg = qualityData?.compensation_kg ?? qualityData?.preliminary_compensation_kg ?? 0;
    calculatedNetKgPayable = (inspectionData.receivedQuantityKg ?? procurement.total_quantity ?? 0) - compensationKg;
    
    console.log(`[WAREHOUSE] Net payable calculation:`, {
      receivedQuantityKg: inspectionData.receivedQuantityKg,
      compensationKg,
      calculatedNetKgPayable
    });

    // --- Prepare warehouse fields update ---
    const warehouseFields: any = {
      received_quantity_kg: inspectionData.receivedQuantityKg ?? null,
      net_kg_payable: calculatedNetKgPayable,
      inspection_notes: inspectionData.inspectionNotes ?? null,
    };
    if (action === "confirm") {
      warehouseFields.warehouse_confirmed_by = userId;
      warehouseFields.warehouse_confirmation_date = new Date().toISOString();
    }
    if (action === "reject") {
      warehouseFields.warehouse_rejected_by = userId;
      warehouseFields.warehouse_rejection_date = new Date().toISOString();
      warehouseFields.warehouse_rejection_reason = inspectionData.rejectionReason ?? "";
    }

    // Check if warehouse inspection row exists:
    const { data: warehouseRec, error: warehouseSelectErr } = await supabase
      .from("procurement_warehouse")
      .select("id")
      .eq("procurement_id", procurement.id)
      .maybeSingle();

    if (warehouseSelectErr) {
      console.error("[WAREHOUSE] Error checking warehouse record:", warehouseSelectErr);
      throw new Error(`Failed to check warehouse record: ${warehouseSelectErr.message}`);
    }

    // Update or insert warehouse record
    let warehouseRecordId: string | null = warehouseRec?.id || null;
    
    if (warehouseRec) {
      const { error: warehouseUpdateErr } = await supabase
        .from("procurement_warehouse")
        .update(warehouseFields)
        .eq("procurement_id", procurement.id);
      if (warehouseUpdateErr) {
        console.error("Warehouse update failed", warehouseUpdateErr);
        throw new Error(`Warehouse update failed: ${warehouseUpdateErr.message}`);
      }
    } else {
      const { data: insertedWarehouse, error: warehouseInsertErr } = await supabase
        .from("procurement_warehouse")
        .insert({ procurement_id: procurement.id, ...warehouseFields })
        .select('id')
        .single();
      if (warehouseInsertErr) {
        console.error("Warehouse insert failed", warehouseInsertErr);
        throw new Error(`Warehouse insert failed: ${warehouseInsertErr.message}`);
      }
      warehouseRecordId = insertedWarehouse?.id || null;
    }

    // Save item-level warehouse data for multi-item procurements
    if (itemWarehouseData && itemWarehouseData.length > 0) {
      console.log('[WAREHOUSE] Saving item-level warehouse data:', itemWarehouseData.length, 'items');
      
      for (const itemData of itemWarehouseData) {
        // Check if record exists
        const { data: existingItemWarehouse } = await supabase
          .from('procurement_item_warehouse')
          .select('id')
          .eq('procurement_item_id', itemData.itemId)
          .maybeSingle();

        const itemWarehouseFields = {
          procurement_item_id: itemData.itemId,
          procurement_warehouse_id: warehouseRecordId,
          received_quantity_kg: itemData.receivedQuantityKg || 0,
          net_payable_kg: itemData.netPayableKg || 0,
          warehouse_notes: itemData.notes || null
        };

        if (existingItemWarehouse) {
          const { error: itemUpdateErr } = await supabase
            .from('procurement_item_warehouse')
            .update(itemWarehouseFields)
            .eq('id', existingItemWarehouse.id);
          if (itemUpdateErr) {
            console.error('[WAREHOUSE] Item warehouse update failed:', itemUpdateErr);
          }
        } else {
          const { error: itemInsertErr } = await supabase
            .from('procurement_item_warehouse')
            .insert(itemWarehouseFields);
          if (itemInsertErr) {
            console.error('[WAREHOUSE] Item warehouse insert failed:', itemInsertErr);
          }
        }
      }
      console.log('[WAREHOUSE] Item-level warehouse data saved successfully');

      // Record inventory inbound transactions AND create batch layers for confirmed items
      if (action === "confirm") {
        console.log('[WAREHOUSE] Recording inventory inbound with batch layers for multi-item procurement...');
        
        // Fetch procurement items to get product_id and price_per_unit
        const { data: procurementItems } = await supabase
          .from('procurement_items')
          .select('id, product_id, price_per_unit')
          .eq('procurement_id', procurement.id);
        
        if (procurementItems) {
          for (const itemData of itemWarehouseData) {
            const procItem = procurementItems.find(pi => pi.id === itemData.itemId);
            if (procItem && procItem.product_id && (itemData.netPayableKg || 0) > 0) {
              try {
                const result = await recordInboundWithBatchLayer({
                  product_id: procItem.product_id,
                  quantity: itemData.netPayableKg || 0,
                  unit_cost: procItem.price_per_unit || 0,
                  procurement_id: procurement.id,
                  procurement_item_id: itemData.itemId,
                  reference_number: `PROC-${procurement.id.slice(0,8)}`,
                  notes: `Received from procurement`
                });
                console.log(`[WAREHOUSE] Recorded inbound with batch layer ${result.batchLayerId} for product ${procItem.product_id}: ${itemData.netPayableKg}kg @ ${procItem.price_per_unit}`);
              } catch (invError) {
                console.error('[WAREHOUSE] Failed to record inventory transaction:', invError);
                // Don't block warehouse confirmation if inventory recording fails
              }
            }
          }
        }
      }
    } else if (action === "confirm") {
      // For single-item procurements, record inventory from main warehouse fields
      console.log('[WAREHOUSE] Recording inventory inbound with batch layer for single-item procurement...');
      
      const { data: singleItem } = await supabase
        .from('procurement_items')
        .select('id, product_id, price_per_unit')
        .eq('procurement_id', procurement.id)
        .maybeSingle();
      
      if (singleItem && singleItem.product_id && calculatedNetKgPayable > 0) {
        try {
          const result = await recordInboundWithBatchLayer({
            product_id: singleItem.product_id,
            quantity: calculatedNetKgPayable,
            unit_cost: singleItem.price_per_unit || 0,
            procurement_id: procurement.id,
            procurement_item_id: singleItem.id,
            reference_number: `PROC-${procurement.id.slice(0,8)}`,
            notes: `Received from procurement`
          });
          console.log(`[WAREHOUSE] Recorded single-item inbound with batch layer ${result.batchLayerId}: ${calculatedNetKgPayable}kg @ ${singleItem.price_per_unit}`);
        } catch (invError) {
          console.error('[WAREHOUSE] Failed to record single-item inventory:', invError);
        }
      }
    }

    // --- Main procurement status update (this is the critical part) ---
    const updateFields: any = { 
      status: newStatus, 
      updated_at: new Date().toISOString(),
      previous_status: procurement.status 
    };
    if (action === "reject") {
      updateFields.rejection_source = "warehouse";
      updateFields.rejected_by = userId;
    }
    
    console.log("[PROCUREMENT] About to update procurement status:", {
      procurement_id: procurement.id,
      old_status: procurement.status,
      new_status: newStatus,
      payment_type: paymentType,
      updateFields
    });

    const { error: updateErr, data: updatedRows } = await supabase
      .from("procurements")
      .update(updateFields)
      .eq("id", procurement.id)
      .select();

    if (updateErr) {
      console.error("Procurement status update failed", updateErr);
      throw new Error(`Procurement status update failed: ${updateErr.message}`);
    }
    
    if (!updatedRows || updatedRows.length === 0) {
      console.error("[PROCUREMENT] No rows updated in procurement table!", {
        procurement_id: procurement.id,
        updateFields,
        userId,
      });
      throw new Error(
        "Procurement status update failed: No rows updated. Your account might lack update permissions or the procurement does not exist."
      );
    }

    console.log("[PROCUREMENT] Successfully updated procurement:", updatedRows[0]);

    // --- Create audit trail entry ---
    try {
      await createProcurementAudit({
        procurement_id: procurement.id,
        action: action === "confirm" ? "warehouse_confirmed" : "warehouse_rejected",
        old_status: oldStatus,
        new_status: newStatus,
        changed_by: userId,
        change_reason: action === "reject" ? inspectionData.rejectionReason : "Warehouse inspection completed",
        metadata: {
          warehouse_action: action,
          payment_type: paymentType,
          received_quantity_kg: inspectionData.receivedQuantityKg,
          compensation_kg: compensationKg,
          net_kg_payable: calculatedNetKgPayable,
          inspection_notes: inspectionData.inspectionNotes,
          warehouse_fields: warehouseFields
        }
      });
      console.log(`[AUDIT] Successfully created audit trail for ${action} action`);
    } catch (auditError) {
      console.error(`[AUDIT] Failed to create audit trail:`, auditError);
      // Don't throw here as the main operation succeeded, but log the error
    }

    // --- Send Telegram Notification ---
    try {
      console.log('[WAREHOUSE] Sending Telegram notification for warehouse action');
      const fullProcurement = await getProcurementById(procurement.id);
      if (fullProcurement) {
        console.log('Enriched procurement data retrieved for warehouse notification:', {
          id: fullProcurement.id,
          product_name: fullProcurement.product_name,
          total_quantity: fullProcurement.total_quantity,
          total_amount: fullProcurement.total_amount,
          status: newStatus
        });
        
        const notificationMessage = formatStatusChangeNotification(fullProcurement, oldStatus, newStatus);
        sendTelegramNotification(notificationMessage, procurement.id)
          .then((result) => {
            if (result.success && result.sent > 0) {
              console.log(`Telegram notification sent to ${result.sent} recipients for warehouse ${action}`);
            }
          })
          .catch(error => {
            console.error('Error sending Telegram warehouse notification:', error);
          });
      } else {
        console.error('Failed to fetch enriched procurement data for warehouse notification');
      }
    } catch (error) {
      console.error('Error fetching procurement for warehouse notification:', error);
    }

    const updated = { id: procurement.id, status: newStatus };
    console.log("[PROCUREMENT] Warehouse status updated successfully", {
      id: procurement.id,
      from: procurement.status,
      to: newStatus,
      action,
      paymentType,
      receivedQuantityKg: inspectionData.receivedQuantityKg,
      compensationKg,
      netKgPayable: calculatedNetKgPayable,
      actualDatabaseUpdate: updatedRows[0]?.status,
      auditTrailCreated: true
    });

    return updated;

  } catch (error) {
    console.error("[WAREHOUSE] Error during warehouse status update:", error);
    throw error;
  }
}
